<template>
    <div class="editor_scene">
        <template v-if="loading">
            <Loading/>
        </template>
        <div class="editor_scene-title">
            <h1>{{ $t('editorProjectsTitle') }}</h1>
        </div>
        <div class="projects">
            <div
                v-for="(project, index) in projects"
                :key="index"
                class="projects-item"
                
                :id=project.id
                @click="(e) => {addProjectData(e, project)}"
            >
                <img v-if="project.thumbnailUrl" :src="project.thumbnailUrl" />
                <img v-else src="@/assets/img/image-placeholder.png" />
                <button
                    class="delete_btn btn"
                    @click.stop="checkIfProjectElementsEmpty(project)"
                >
                    {{ $t('editorDelete') }}
                </button>
                <button
                    class="edit_btn btn"
                    @click.stop="editProject(project)"
                >
                    {{ $t('editorEdit') }}
                </button>
                <div class="projects-item-title">
                    {{ project.name }}
                    <p v-html="excerpt(project.description)"></p>
                    <div
                        :class="['projects-item-tooltip', project.description === '' ? 'no-description' : '']"
                        :style="colors"
                    >
                        <span>{{ project.name }}</span>
                        <p>{{ project.description }}</p>
                    </div>
                </div>
                
            </div>
            <div class="projects-item projects-item-new">
                <button
                    class="add_new-btn btn"
                    @click="createNewProject"
                >
                    <inline-svg
                        :src="add_icon"
                        width="45px"
                        height="45px"
                    />
                </button>
                <div class="projects-item-title">
                    {{ $t('editorProjectsCreateNew') }}
                </div>
            </div>
        </div>
        <Modal 
            v-if="showFormModal"
            @close="showFormModal = false"
            :showModal="false"
        >
            <template v-slot:header>
                <strong>{{ $t('editorProjectsCreateNew') }}</strong>
            </template>
            <template v-slot:body>
                <form method="post" action="?" @submit.prevent="submit" class="modalForm">
                    <div class="form-group">
                        <label for="projectName">{{ $t('editorProjectsName') }}</label>
                        <input
                            id="projectName"
                            v-model="projectName"
                            type="text"
                            name="projectName"
                        />
                    </div>
                    <div class="form-group">
                        <label for="projectDescription">{{ $t('editorProjectsDescription') }}</label>
                        <textarea
                            id="projectDescription"
                            v-model="projectDescription"
                            type="text"
                            name="projectDescription"
                            placeholder="Set project description"
                        />
                    </div>
                    <div class="form-footer">
                        <button @click="closeProjectModal" class="button modal_cancel-btn">{{ $t('editorCancel') }}</button>
                        <button
                            type="submit"
                            class="button main_btn form_submit_btn"
                        >{{ $t('editorSubmit') }}</button>
                    </div>
                </form>
            </template>
        </Modal>
        <Modal 
            v-if="showDeleteModal"
            @close="closeWarningModal"
        >
            <template v-slot:header>
                
            </template>
            <template v-slot:body>
                {{ $t('editorProjectsWarning') }}
                <button
                    class="btn main_btn"
                    @click="removeProject(projectToRemove)"
                >{{ $t('editorProjectsRemove') }}</button>
            </template>
        </Modal>
        <Modal 
            v-if="showEditModal"
        >
            <template v-slot:header>
                <strong>{{ $t('editorProjectEdit') }}</strong>
            </template>
            <template v-slot:body>
                <form method="post" action="?" @submit.prevent="edit" class="modalForm">
                    <div class="form-group">
                        <label for="editProjectname">{{ $t('editorProjectsName') }}</label>
                        <input
                            id="editProjectname"
                            v-model="projectName"
                            type="text"
                            name="editProjectname"
                        />
                    </div>
                    <div class="form-group">
                        <label for="editProjectDescription">{{ $t('editorProjectsDescription') }}</label>
                        <textarea
                            id="editProjectDescription"
                            v-model="projectDescription"
                            type="text"
                            name="editProjectDescription"
                            placeholder="Set project description"
                        />
                    </div>
                    <div class="form-footer">
                        <button @click="closeEditProjectModal" class="button modal_cancel-btn">{{ $t('editorCancel') }}</button>
                        <button
                            type="submit"
                            class="button main_btn form_submit_btn"
                        >{{ $t('editorSubmit') }}</button>
                    </div>
                </form>
            </template>
        </Modal>
    </div>
</template>

<script>
    import { uuid } from 'vue-uuid';
    import { mapGetters, mapState } from 'vuex';
    import { RepositoryFactory } from '../../repositories/repositoryFactory';
    const FormatRepository = RepositoryFactory.get('formats');
    const ProjectRepository = RepositoryFactory.get('projects');
    import { migarateToLatest } from '../../js/migrator.js';

    import Modal from '@/components/Global/Modal.vue';
    import Loading from '@/components/Global/Loading.vue';
    import add_icon from '@/assets/icons/add.svg';

    export default {
        data() {
            return {
                projects: [],
                showFormModal: false,
                projectName: null,
                projectDescription: null,
                projectId: null,
                formatId: null,
                loading: true,
                add_icon,
                showDeleteModal: false,
                projectToRemove: null,
                showEditModal: false
            }
        },
        methods: {
            async addProjectData(e, project) {
                const headers = {
                    'api-key' : 'session',
                    'api-sign' : this.getSessionDetails.token
                }

                try {
                    const projectData = await ProjectRepository.getProject(project.id, headers);

                    const promises = projectData.data.data.pages.map(page => {
                        return ProjectRepository.getProjectPage(project.id, page.number, headers);
                    });

                    const pagesDetails = (await Promise.all(promises)).sort((a, b) => parseFloat(a.data.data.number) - parseFloat(b.data.data.number)).map((page, index) => {
                        const isActive = index === 0 ? true : false;
                        const pageContent = migarateToLatest(page.data.data.content);
                
                        return {
                        ...page.data.data,
                        content: {
                            ...pageContent,
                            isActive,
                            elements: []
                        }
                        }
                    });

                    const data = {
                        id: projectData.data.data.id,
                        name: projectData.data.data.name,
                        templateFormatId: projectData.data.data.templateFormat.id,
                        isPublished: projectData.data.data.isPublished,
                        pages: pagesDetails,
                        setup: projectData.data.data.setup
                    }

                    const activePageId = data.pages.find(page => page.content.isActive === true).content.id;

                    const activeFormatThemeTab = {
                        id: projectData.data.data.templateFormat.id,
                        name: projectData.data.data.templateFormat.name,
                        width: projectData.data.data.templateFormat.pageWidth,
                        height: projectData.data.data.templateFormat.pageHeight
                    }
                    this.$store.dispatch('SET_ACTIVE_FORMAT_IN_THEME_TAB', activeFormatThemeTab);
                    this.$store.dispatch('SET_ACTIVE_PROJECT_IN_THEME_TAB', data.id);

                    this.$store.dispatch('ADD_PROJECT_DATA', data).then(() => {
                        this.$router.replace({ name: 'editProject', params: { projectName: projectData.data.data.name } })
                    });

                } catch (err) {
                    console.log(err);
                }
               
                this.loading = false;
            },
            createNewProject() {
                this.showFormModal = true;
            },
            async submit() {
                const newPageId = uuid.v1();
                const pageData = {
                    html: null,
                    content: {
                        id: newPageId,
                        zIndex: 0,
                        isActive: true,
                        elements: [],
                        pageColor: {
                            rgb: 'rgb(255, 255, 255)',
                            hex: 'FFFFFF',
                            X: 0,
                            Y: 150
                        },
                        pageRadius: 0,
                        isLocked: false,
                        version: 1.0,
                        backgroundImage: {
                            url: null,
                            backgroundPosition: 'center',
                            backgroundSize: 'cover',
                            backgroundRepeat: 'no-repeat'
                        }
                    }
                };
                const payload = {
                    templateFormatId: JSON.parse(JSON.stringify(this.getTemplateFormat)).id,
                    name: this.projectName,
                    description: this.projectDescription,
                    pages: [pageData]
                }
                const headers = {
                    'api-key' : 'session',
                    'api-sign' : this.getSessionDetails.token
                }

                this.loading = true;

                try {
                    const createProject = await ProjectRepository.createProject(payload, headers);
                    console.log('project: ', createProject)
                    const pages = [{
                            ...createProject.data.data.pages[0],
                            ...pageData
                        } 
                    ]
                    const data = {
                        id: createProject.data.data.id,
                        name: createProject.data.data.name,
                        isPublished: createProject.data.data.isPublished,
                        pages,
                        setup: {
                            hiddenTabs: {
                                ThemeTab: false,
                                ImageTab: false,
                                PhotoCellTab: false,
                                TextTab: false,
                                ShapeTab: false,
                                PlaceholderTab: false
                            }
                        }
                    }
                    
                    this.$store.dispatch('SET_ACTIVE_PROJECT_IN_THEME_TAB', null);
                    this.$store.dispatch('SET_ACTIVE_FORMAT_IN_THEME_TAB', { id: this.getTemplateFormat.id, name: this.getTemplateFormat.name });
                    this.$store.dispatch('ADD_PROJECT_DATA', data).then(() => {
                        this.$router.push({ name: 'editProject', params: { projectName: this.projectName } })
                    })
                }
                catch(err) {
                    console.log(err)
                }

                this.loading = false;
            },
            async checkIfProjectElementsEmpty(project) {
                const headers = {
                    'api-key' : 'session',
                    'api-sign' : this.getSessionDetails.token
                }
                try {
                    
                    const projectData = await ProjectRepository.getProject(project.id, headers);

                    const promises = projectData.data.data.pages.map(page => {
                        return ProjectRepository.getProjectPage(project.id, page.number, headers);
                    })

                    const pagesData =  (await Promise.all(promises)).map(page => {
                        return page.data.data.content.elements
                    });

                    pagesData.length > 0 ? (this.showDeleteModal = true, this.projectToRemove = project.id) : this.removeProject(project.id);

                } catch (err) {
                    // Handle errors
                }
            },
            removeProject(projectId) {
                const headers = {
                    'api-key' : 'session',
                    'api-sign' : this.getSessionDetails.token
                }
                ProjectRepository.removeProject(projectId, headers)
                    .finally(() => {
                        this.showDeleteModal = false;
                    })

                this.projects = this.projects.filter(project => project.id != projectId)
            },
            closeWarningModal() {
                this.showDeleteModal = false;
                this.projectToRemove = null;
            },
            closeProjectModal() {
                this.projectName = null;
                this.projectDescription = null;
                this.showFormModal = false;
            },
            editProject(project) {
                this.projectId = project.id;
                this.projectName = project.name;
                this.projectDescription = project.description;
                this.showEditModal = true;
            },
            edit() {
                const headers = {
                    'api-key' : 'session',
                    'api-sign' : this.getSessionDetails.token
                }

                ProjectRepository.saveProject({ id: this.projectId, name: this.projectName, description: this.projectDescription }, headers)
                    .then(response => {
                        let editedProjectObject = this.projects.find(project => project.id === this.projectId);
                        editedProjectObject.name = this.projectName;
                        editedProjectObject.description = this.projectDescription;
                    })
                    .catch(error => {
                        console.log(error)
                    })
                    .finally(() => {
                        this.projectId = null;
                        this.projectName = null;
                        this.projectDescription = null;
                        this.showEditModal = false;
                    });
            },
            closeEditProjectModal() {
                this.projectId = null;
                this.projectName = null;
                this.projectDescription = null;
                this.showEditModal = false;
            },
            excerpt(string) {
                if (string.length > 0) {
                    return string.split(' ').splice(0, 6).join(' ') + '...';
                }
                
            }
        },
        computed: {
            ...mapGetters([
                'getTemplateFormat',
                'getSessionDetails'
            ]),
            colors() {
                return {
                    '--primary-color': `#${this.getSessionDetails.editorSetup.colors.primaryColor}`
                }
            }
        },
        async created() {
            try {
                const formatId = JSON.parse(JSON.stringify(this.getTemplateFormat)).id;

                const headers = {
                    'api-key' : 'session',
                    'api-sign' : this.getSessionDetails.token
                }

                const listProjectsResponse = await ProjectRepository.listProjects(formatId, headers);

                const projects = listProjectsResponse.data.data.entities;

                projects.forEach(entity => {
                    this.projects = [
                        ...this.projects,
                        entity
                    ]
                });

                const promises = this.projects.map(project => {
                    return ProjectRepository.getProjectPage(project.id, 1, headers);
                });

                const pages = await Promise.all(promises);

                this.projects.map((project, index) => {
                    return project.thumbnailUrl = pages[index].data.data.thumbnailUrl
                })
                

                this.loading = false;

            }
            catch(err) {
                console.log(err)
            }
        },
        components: {
            Modal,
            Loading
        }
    }
</script>

<style lang="scss" scoped>
    @import '@/styles/setup/_variables.scss';
    
    .editor_scene {
        padding: $pd-s;
        overflow: auto;
    }
    .projects-item {
        position: relative;
        > img {
            width: 100%;
        }

        &-new {
            display: flex;
            flex-direction: column;
        }

        &-title {
            position: relative;

            > p {
                font-size: 12px;
                margin-top: 10px;
                line-height: 20px;
            }
        }

        &-tooltip {
            display: none;
            position: absolute;
            top: 0;
            width: 250px;
            background-color: var(--primary-color);
            color: #fff;
            padding: 15px;
            box-sizing: border-box;
            border-radius: 5px;
            z-index: 100;

            &.no-description {
                width: 100%;
            }

            span {
                margin-bottom: 15px;
                display: block;
            }
        }
        &:hover {
            .projects-item-tooltip {
                display: block;
            }
        }
    }
    .edit_btn {
        width: auto;
        height: auto;
        position: absolute;
        background: #fff;
        padding: 3px 5px;
        left: 0px;
        top: 0px;
        color: #310048;
        border: 0;
        z-index: 1;
        border-radius: 0;
        display: none;
    }
    .edit_btn:hover {
        background: #ddd;
    }
    .delete_btn {
        width: auto;
        height: auto;
        position: absolute;
        background: #E91E63;
        padding: 3px 5px;
        right: 0px;
        top: 0px;
        color: #fff;
        border: 0;
        z-index: 1;
        border-radius: 0;
        display: none;
    }
    .delete_btn:hover {
        background: #CA1150;
    }
    .projects-item:hover .edit_btn,
    .projects-item:hover .delete_btn {
        display: block;
    }
    .projects-item-box {
        position: relative;
        background-size: cover;
        background-repeat: no-repeat;
        background-position: center center;
    }
    textarea {
        resize: none;
    }
</style>